import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { url } from '../../url';
import Page from '../../components/Page';
import { Post } from './types';
import PostCard from './components/PostCard';
import CreatePost from './components/CreatePost';
import FeedHeader from './components/FeedHeader';
import styles from './Feed.module.scss';

const Feed: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [myProjects, setMyProjects] = useState<any[]>([]);
  const [mySkills, setMySkills] = useState<string[]>([]);
  const [latestProjects, setLatestProjects] = useState<any[]>([]);
  const [latestSkillers, setLatestSkillers] = useState<any[]>([]);
  const navigate = useNavigate();

  const fetchPosts = async (pageNum: number = 1) => {
    try {
      const response = await axios.get(`${url}/post?page=${pageNum}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = response.data;
      
      if (pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts(prev => [...prev, ...(data.posts || [])]);
      }
      
      setHasMore(data.hasMore || false);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        // Projekty
        const projectsRes = await axios.get(url + '/user/myProjects?page=1', {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        setMyProjects(projectsRes.data.UserProjects || []);
        // Skille
        const skillsRes = await axios.get(url + '/user/myTeammate', {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        const teammate = skillsRes.data.userTeammateProfile;
        if (teammate && teammate.hardSkills && Array.isArray(teammate.hardSkills)) {
          setMySkills(teammate.hardSkills);
        } else {
          setMySkills([]);
        }
        // Najnowsze projekty
        const fetchLatestProjects = async () => {
          try {
            const res = await axios.get(url + '/project?page=1', {
              headers: { 'Content-Type': 'application/json' },
            });
            setLatestProjects(res.data.projects?.slice(0, 5) || []);
          } catch (err) {
            setLatestProjects([]);
          }
        };
        // Najnowsi skillerzy
        const fetchLatestSkillers = async () => {
          try {
            const res = await axios.get(url + '/user/users', {
              headers: { 'Content-Type': 'application/json' },
            });
            setLatestSkillers(res.data.users?.slice(0, 5) || []);
          } catch (err) {
            setLatestSkillers([]);
          }
        };
        fetchLatestProjects();
        fetchLatestSkillers();
      } catch (err) {
        setMyProjects([]);
        setMySkills([]);
      }
    };
    fetchUserData();
  }, []);

  const handleCreatePost = async (content: string, file?: File) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }

    try {
      const postData = {
        data: content,
        image: file ? await convertFileToBase64(file) : undefined,
      };

      const response = await axios.post(`${url}/post`, postData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      const newPost = response.data.post;
      setPosts(prev => [newPost, ...prev]);
    } catch (error) {
      console.error('Error creating post:', error);
      throw error; // Re-throw so CreatePost component can handle it
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Helper function to get user ID from token
  const getUserIdFromToken = (token: string): string | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const handleLike = async (postId: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to like posts');
      return;
    }

    const userId = getUserIdFromToken(token);
    if (!userId) {
      console.error('Could not decode user ID from token');
      return;
    }

    try {
      await axios.post(`${url}/post/${postId}/like`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      // Update local state
      setPosts(prev => prev.map(post =>
        post._id === postId
          ? { ...post, likes: [...post.likes, userId] }
          : post
      ));
    } catch (error) {
      console.error('Error liking post:', error);
      alert('Failed to like post. Please try again.');
    }
  };

  const handleUnlike = async (postId: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to unlike posts');
      return;
    }

    const userId = getUserIdFromToken(token);
    if (!userId) {
      console.error('Could not decode user ID from token');
      return;
    }

    try {
      await axios.delete(`${url}/post/${postId}/like`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      // Update local state
      setPosts(prev => prev.map(post =>
        post._id === postId
          ? { ...post, likes: post.likes.filter(id => id !== userId) }
          : post
      ));
    } catch (error) {
      console.error('Error unliking post:', error);
      alert('Failed to unlike post. Please try again.');
    }
  };

  const handleComment = async (postId: string, content: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to comment on posts');
      return;
    }

    try {
      const response = await axios.post(`${url}/post/${postId}/comments`, {
        data: content
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      const newComment = response.data.comment;
      setPosts(prev => prev.map(post =>
        post._id === postId
          ? { ...post, comments: [...post.comments, newComment] }
          : post
      ));
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    }
  };

  const handleShare = (postId: string) => {
    // Share functionality not implemented in backend yet
    console.log('Share functionality not implemented yet');
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <Page>
        <div className={styles.feedContainer}>
          <FeedHeader />
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner}></div>
            <p>Loading posts...</p>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div className={styles.feedContainer}>
        <FeedHeader />
        <div className={styles.feedLayout}>
          {/* Lewa kolumna */}
          <aside className={styles.feedSidebarLeft}>
            {/* Profil/logowanie */}
            {localStorage.getItem('token') ? (
  null
              // <div className={styles.profileBox}>
              //   <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=160&h=160&fit=crop&crop=face" alt="User Avatar" className={styles.profileAvatar} />
              //   <div className={styles.profileName}>Adrian Kazi</div>
              //   <div className={styles.profileRole}>Consultant / Data Science</div>
              //   <div className={styles.profileLocation}>Warszawa, Woj. Mazowieckie</div>
              //   <div className={styles.profileCompany}><img src="https://upload.wikimedia.org/wikipedia/commons/8/8e/Accenture.svg" alt="Accenture" style={{height:16,verticalAlign:'middle',marginRight:4}}/>Accenture</div>
              // </div>
            ) : (
              <div className={styles.profileBox}>
                <div className={styles.profileName}>Zaloguj się, aby zobaczyć swój profil</div>
                <button className={styles.loginBtn} onClick={() => navigate('/login')}>Zaloguj się</button>
              </div>
            )}
            {/* Moje projekty */}
            {localStorage.getItem('token') && myProjects.length > 0 && (
              <div className={styles.projectsBox}>
                <div className={styles.projectsTitle}>Moje projekty</div>
                <ul className={styles.projectsList}>
                  {myProjects.slice(0, 5).map((project) => (
                    <li key={project._id} style={{cursor: 'pointer'}} onClick={() => navigate('/projects/my')}>
                      {project.projectName || project.name || 'Projekt'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Moje umiejętności */}
            {localStorage.getItem('token') && mySkills.length > 0 && (
              <div className={styles.skillsBox}>
                <div className={styles.skillsTitle}>Moje umiejętności</div>
                <ul className={styles.skillsList}>
                  {mySkills.slice(0, 8).map((skill, idx) => (
                    <li key={idx} style={{cursor: 'pointer'}} onClick={() => navigate('/skillers/my')}>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Najnowsze projekty */}
            {latestProjects.length > 0 && (
              <div className={styles.projectsBox}>
                <div className={styles.projectsTitle}>Najnowsze projekty</div>
                <ul className={styles.projectsList}>
                  {latestProjects.map((project) => (
                    <li key={project._id} style={{cursor: 'pointer'}} onClick={() => navigate(`/projects/${project._id}`)}>
                      {project.projectName || project.name || 'Projekt'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Najnowsi skillerzy */}
            {latestSkillers.length > 0 && (
              <div className={styles.skillsBox}>
                <div className={styles.skillsTitle}>Najnowsi skillerzy</div>
                <ul className={styles.skillsList}>
                  {latestSkillers.map((user) => (
                    <li key={user._id} style={{cursor: 'pointer'}} onClick={() => navigate(`/skillers/${user._id}`)}>
                      {user.username || user.name || 'Skiller'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Co nowego na mobile */}
            {isMobile && (
              <div className={styles.contentBox}>
                <div className={styles.contentTitle}>Co nowego?</div>
                <ul className={styles.contentList}>
                  <li>Dołączył nowy użytkownik: <b>Anna Nowak</b></li>
                  <li>Witamy <b>Piotra Zielińskiego</b> w społeczności!</li>
                  <li>Brak reklam – korzystaj bez przeszkód!</li>
                </ul>
              </div>
            )}
          </aside>
          {/* Środkowa kolumna (feed) */}
          <main className={styles.feedMain}>
            <CreatePost onCreatePost={handleCreatePost} />
            <div className={styles.postsList}>
              {posts.map(post => (
                <PostCard
                  key={post._id}
                  post={post}
                  onLike={handleLike}
                  onUnlike={handleUnlike}
                  onComment={handleComment}
                  onShare={handleShare}
                  currentUserId={getUserIdFromToken(localStorage.getItem('token') || '') || ''}
                />
              ))}
              {hasMore && (
                <button 
                  className={styles.loadMoreBtn}
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More Posts'}
                </button>
              )}
            </div>
          </main>
          {/* Prawa kolumna tylko na desktopie */}
          {!isMobile && (
            <aside className={styles.feedSidebarRight}>
              <div className={styles.contentBox}>
                <div className={styles.contentTitle}>Co nowego?</div>
                <ul className={styles.contentList}>
                  <li>Dołączył nowy użytkownik: <b>Anna Nowak</b></li>
                  <li>Witamy <b>Piotra Zielińskiego</b> w społeczności!</li>
                  <li>Brak reklam – korzystaj bez przeszkód!</li>
                </ul>
              </div>
              <div className={styles.newsBox}>
                <div className={styles.newsTitle}>Aktualności</div>
                <ul className={styles.newsList}>
                  <li>Nowa funkcja: Feed społecznościowy!</li>
                  <li>Dołącz do grup tematycznych</li>
                  <li>Wkrótce: system rekomendacji projektów</li>
                </ul>
              </div>
            </aside>
          )}
        </div>
      </div>
    </Page>
  );
};

export default Feed; 